import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import logger from "../../utils/logger";
import FileValidationService from "../../services/fileValidation.service";

export default function createUploadService() {
    // Private data cache
    let data = [];
    let isLoading = false;
    let pendingLoad = null;
    let currentFileType = null;

    function getData() {
        return [...data]; // Return a copy to prevent external mutation
    }

    function loadData(forceRefresh = false) {
        // Reuse the in-flight request so callers don't race against an empty cache.
        if (isLoading && pendingLoad) {
            return pendingLoad.then((result) => [...result]);
        }

        // If data exists and we're not forcing a refresh, return the cached data
        if (data.length > 0 && !forceRefresh) {
            isLoading = false; // Ensure loading state is reset when returning cached data
            Eventbus.emit(events.FILETYPES_LOADED, [...data]);
            return Promise.resolve([...data]);
        }

        isLoading = true;

        // Otherwise fetch fresh data from the API
        pendingLoad = api
            .getFileTypes()
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return [...data]; // Return existing data on cancellation
                }
                data = response || []; // Store the response data in the data variable
                Eventbus.emit(events.FILETYPES_LOADED, [...data]);
                return [...data];
            })
            .catch((error) => {
                logger.error('Error fetching file types:', error);
                return [];
            })
            .finally(() => {
                isLoading = false;
                pendingLoad = null;
            });

        return pendingLoad;
    }

    function init() {
        return loadData(true);
    }

    function clear() {
        data = [];
        pendingLoad = null;
        currentFileType = null;
    }



    function getLoadingState() {
        return isLoading;
    }

    function uploadFile(file, options = {}) {
        if (!currentFileType) {
            logger.error("No file type detected for upload");
            return Promise.reject(new Error("No file type detected"));
        }
        if(currentFileType.code === 'PAY'){
            return api.uploadMerchantFile(file, options?.selectedOption?.id, file.name);
        }
        if(currentFileType.code === 'WIND'){
            return api.uploadWindcaveFile(file, options?.selectedOption?.id);
        }
        return api.uploadFile(file, currentFileType.code)
            .then(response => {
                logger.debug("File uploaded successfully:", response);
                return response;
            });
    }

    function uploadPdfFile(file, postingDate, transactionId = null) {
        if (!file) {
            logger.error("No PDF file provided for upload");
            return Promise.reject(new Error("No PDF file provided"));
        }
        if (!postingDate) {
            logger.error("No posting date provided for PDF upload");
            return Promise.reject(new Error("No posting date provided"));
        }
        return api.uploadPdfFile(file, postingDate, transactionId)
            .then(response => {
                logger.debug("PDF file uploaded successfully:", response);
                return response;
            });
        // Note: Error handling is done automatically by the API interceptor
    }

    function uploadDirectDebitFile(file, postingDate) {
        if (!file) {
            logger.error("No direct debit file provided for upload");
            return Promise.reject(new Error("No direct debit file provided"));
        }
        if (!postingDate) {
            logger.error("No posting date provided for direct debit upload");
            return Promise.reject(new Error("No posting date provided"));
        }
        return api.uploadDirectDebitFile(file, postingDate)
            .then(response => {
                logger.debug("Direct debit file uploaded successfully:", response);
                return response;
            });
        // Note: Error handling is done automatically by the API interceptor
    }

    function getNzPostFile(date) {
        if (!date) {
            logger.error("No date provided for NZ Post file fetch");
            return Promise.reject(new Error("Date is required"));
        }
        return api.getNzPostFile(date)
            .then(response => {
                logger.debug("NZ Post file fetched successfully:", response);
                // Decode the base64 content to text for display
                if (response && response.contentBase64) {
                    const decodedContent = atob(response.contentBase64);
                    return {
                        ...response,
                        decodedContent: decodedContent
                    };
                }
                return response;
            });
        // Note: Error handling is done automatically by the API interceptor
    }

    function uploadNzPostFile(nzPostData, transactionId = null) {
        if (!nzPostData || !nzPostData.fileName || !nzPostData.contentBase64) {
            logger.error("No NZ Post data provided for upload");
            return Promise.reject(new Error("NZ Post data with fileName and contentBase64 is required"));
        }
        return api.uploadNzPostFile(nzPostData.fileName, nzPostData.contentBase64, transactionId)
            .then(response => {
                logger.debug("NZ Post file uploaded successfully:", response);
                return response;
            });
        // Note: Error handling is done automatically by the API interceptor
    }

    function getNzPostFiles() {
        return api.getNzPostFiles()
            .then(response => {
                logger.debug("NZ Post files fetched successfully:", response);
                return response;
            });
        // Note: Error handling is done automatically by the API interceptor
    }

    function getNzPostFileContent(filename) {
        if (!filename) {
            logger.error("No filename provided for NZ Post file content fetch");
            return Promise.reject(new Error("Filename is required"));
        }
        return api.getNzPostFileContent(filename)
            .then(response => {
                logger.debug("NZ Post file content fetched successfully:", response);
                // Decode the base64 content to text for display, similar to getNzPostFile
                if (response && response.contentBase64) {
                    const decodedContent = atob(response.contentBase64);
                    return {
                        ...response,
                        decodedContent: decodedContent
                    };
                }
                return response;
            });
        // Note: Error handling is done automatically by the API interceptor
    }

    function checkFileType(file, csvData) {
        if (!file || !csvData || csvData.length === 0) {
            logger.warn("No file or CSV data provided for type check");
            currentFileType = null;
            Eventbus.emit(events.FILETYPE_NOT_MATCHED);
            return null;
        }

        // Filter valid file types first (early elimination)
        // Only exclude file types that have BOTH no bank account AND no headers
        const validFileTypes = data.filter(fileType => {
            const hasHeaders = fileType.requiredHeaders &&
                (fileType.headerRow !== null && fileType.headerRow !== undefined);
            const hasBankAccount = fileType.expectedBankAccNo &&
                (fileType.bankAccRow !== null && fileType.bankAccRow !== undefined);

            // Include if it has headers OR bank account (or both)
            return hasHeaders || hasBankAccount;
        });

        if (validFileTypes.length === 0) {
            logger.warn("No valid file types configured");
            currentFileType = null;
            Eventbus.emit(events.FILETYPE_NOT_MATCHED);
            return null;
        }

        // PHASE 1: Check all bank accounts first across all file types
        let bankAccountMatches = [];

        for (const fileType of validFileTypes) {
            // Parse required headers
            let requiredHeaders = [];
            if (fileType.requiredHeaders) {
                requiredHeaders = fileType.requiredHeaders.split('|').map(h => h.trim().toLowerCase());
            }
            logger.debug(`Checking file type: ${fileType.typeName} (${fileType.code}) with ${requiredHeaders.length} required headers:`, requiredHeaders);

            // Perform complete validation in one call
            const validationResult = FileValidationService.validateFile({
                csvRows: csvData,
                fileType: fileType,
                requiredFields: requiredHeaders
            });

            const headerMatchPercentage = requiredHeaders.length === 0 ? 1.0 : (validationResult.fields.status.filter(f => f.found).length / requiredHeaders.length);
            const matchedHeaders = validationResult.fields.status.filter(f => f.found).map(f => f.name);
            logger.debug(`${fileType.typeName}: ${(headerMatchPercentage * 100).toFixed(1)}% match (${matchedHeaders.length}/${requiredHeaders.length}) - Matched: [${matchedHeaders.join(', ')}]`);

            // Check if this is a perfect match (bank account + 100% headers)
            if (validationResult.isValid && headerMatchPercentage >= 1.0) {
                logger.debug(`File type matched: ${fileType.typeName} (perfect match)`);
                currentFileType = fileType;

                Eventbus.emit(events.FILETYPE_MATCHED, {
                    fileType: currentFileType,
                    fieldNames: requiredHeaders,
                    validation: validationResult
                });

                return currentFileType;
            }

            // Collect all bank account matches for Phase 1
            if (validationResult.bankAccount.isValid && !validationResult.bankAccount.skipped) {
                bankAccountMatches.push({
                    fileType,
                    requiredHeaders,
                    validationResult,
                    headerMatchPercentage
                });
            }
        }

        // If we found any bank account matches, use the best one (highest header match percentage)
        if (bankAccountMatches.length > 0) {
            const bestBankAccountMatch = bankAccountMatches.reduce((best, current) =>
                current.headerMatchPercentage > best.headerMatchPercentage ? current : best
            );

            logger.debug(`Bank account match found: ${bestBankAccountMatch.fileType.typeName}`);

            Eventbus.emit(events.FILETYPE_MATCHED, {
                fileType: bestBankAccountMatch.fileType,
                fieldNames: bestBankAccountMatch.requiredHeaders,
                validation: bestBankAccountMatch.validationResult
            });

            // Only set currentFileType if headers are also complete, otherwise just show validation
            if (bestBankAccountMatch.headerMatchPercentage >= 1.0 || bestBankAccountMatch.requiredHeaders.length === 0) {
                currentFileType = bestBankAccountMatch.fileType;
                return currentFileType;
            }

            return null; // Bank account matched but headers incomplete - upload disabled
        }

        // PHASE 2: No bank account matches found, fallback to header matching
        let bestHeaderMatch = null;
        let bestMatchScore = 0;

        for (const fileType of validFileTypes) {
            // Parse required headers
            let requiredHeaders = [];
            if (fileType.requiredHeaders) {
                requiredHeaders = fileType.requiredHeaders.split('|').map(h => h.trim().toLowerCase());
            }
            
            // Skip file types with no headers in phase 2
            if (requiredHeaders.length === 0) {
                continue;
            }
            
            logger.debug(`Phase 2 - Checking file type: ${fileType.typeName} (${fileType.code}) with ${requiredHeaders.length} required headers`);

            // Perform complete validation in one call  
            const validationResult = FileValidationService.validateFile({
                csvRows: csvData,
                fileType: fileType,
                requiredFields: requiredHeaders
            });

            const headerMatchPercentage = validationResult.fields.status.filter(f => f.found).length / requiredHeaders.length;
            const matchedHeaders = validationResult.fields.status.filter(f => f.found).map(f => f.name);
            logger.debug(`Phase 2 - ${fileType.typeName}: ${(headerMatchPercentage * 100).toFixed(1)}% match (${matchedHeaders.length}/${requiredHeaders.length}) - Matched: [${matchedHeaders.join(', ')}]`);

            // Track the best header match
            if (headerMatchPercentage > bestMatchScore) {
                bestMatchScore = headerMatchPercentage;
                bestHeaderMatch = {
                    fileType,
                    requiredHeaders,
                    validationResult
                };
            }
        }

        // Show best header match if it's reasonable (>80%)
        if (bestHeaderMatch && bestMatchScore > 0.8) {
            logger.debug(`Header match found: ${bestHeaderMatch.fileType.typeName} (${(bestMatchScore * 100).toFixed(0)}% headers)`);

            Eventbus.emit(events.FILETYPE_MATCHED, {
                fileType: bestHeaderMatch.fileType,
                fieldNames: bestHeaderMatch.requiredHeaders,
                validation: bestHeaderMatch.validationResult
            });

            // Only set currentFileType if it's a perfect header match (100%)
            if (bestMatchScore >= 1.0) {
                currentFileType = bestHeaderMatch.fileType;
                return currentFileType;
            }

            return null; // Partial header match - upload disabled
        }

        logger.warn("No matching file type found in either phase");
        currentFileType = null;
        Eventbus.emit(events.FILETYPE_NOT_MATCHED);
        return null;
    }

    function checkFileTypeAsync(file, csvData) {
        return loadData().then(() => checkFileType(file, csvData));
    }

    return {
        getData,
        loadData,
        getLoadingState,
        init,
        clear,
        uploadFile,
        uploadPdfFile,
        uploadDirectDebitFile,
        getNzPostFile,
        uploadNzPostFile,
        getNzPostFiles,
        getNzPostFileContent,
        checkFileType,
        checkFileTypeAsync
    };
}
